import React from "react";
import { useQuestionnairesController } from "./questionnaires";
import { useMessage } from "@common/hooks/message";
import { AllQuestionnairePageGetDto, copyFhq4Questionnaire, createFhq4Questionnaire, deleteFhq4Questionnaire, getDefaultFhq4QuestionnaireForCreating, getFhq4Questionnaire, getFhq4QuestionnairePage, saveFhq4Questionnaire, importQuestionnaire } from "../services/questionnaires";
import { HttpErrorDto } from "@common/dto/http";
import { I18n } from "react-redux-i18n";
import { useQuestionnaireController } from "./questionnaire";
import { QuestionnaireListCompactDto } from "@common/dto/questionnaire/list-compact-dto";
import { GetDatasheetFolderFormats, GetFolderFieldsParams, getDatasheetFolderFormats as getDatasheetFolderFormatsBase, getFolderFields as getFolderFieldsBase } from "@common/actions/formats";
import { FieldType } from "@common/dto/field/type";
import { getDatasheetFolders } from "@common/actions/folders";
import { QuestionnaireManagementDatasheetFolder, QuestionnaireManagementDatasheetFormatListDto } from "../components/questionnaire-management/questionnaire-management.types";
import { QuestionnaireFolderField } from "../types/field";
import { QuestionnaireUpdateLocalDto } from "../dto/questionnaire-update-local-dto";
import { QuestionnairePageUpdateDto } from "@common/dto/questionnaire/page/update-dto";
import { QUESTIONNAIRE_SELECTOR_ATTRIBUTE } from "../components/questionnaires/questionnaires.constants";
import { QuestionnaireCreateLocalDto } from "../dto/questionnaire-create-local-dto";
import { QuestionnaireCreateDto } from "@common/dto/questionnaire/create-dto";
import { useImportQuestionnaireController } from "./import-questionnaire";
import { QuestionnaireUpdateDto } from "@common/dto/questionnaire/update-dto";

export const useController = () => {
  const [globalOperationIsLoading, setGlobalOperationLoadingState] = React.useState(false);
  const [loadingQuestionnaire, setLoadingQuestionnaire] = React.useState(false);
  const questionnairesRef = React.useRef<HTMLDivElement>(null);
  const [selectedQuestionnaireFromList, setSelectedQuestionnaireFromList] = React.useState<QuestionnaireListCompactDto | undefined>(undefined);
  const [questionnaireForManagement, setQuestionnaireForManagement] = React.useState<QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto | undefined>(undefined);

  const currentQuestionnaireIsNotCreated = (questionnaireForManagement as QuestionnaireCreateLocalDto)?.isNotCreated ?? false;

  const {
    showMessage,
    showInfoMessage,
    closeMessage,
    showErrorMessage,
    showConfirmMessage,
    messageProps,
  } = useMessage();

  const {
    questionnaires,
    questionnairesLoading,
    getAndSetQuestionnaires,
    addQuestionnaireToList,
    removeQuestionnaireFromList,
  } = useQuestionnairesController({
    showErrorMessage
  });

  const {
    convertQuestionnaireDefaultCreateDtoToQuestionnaireCreateLocalDto,
    resetPagesLoadedState,
    isPageLoaded,
    markPageLoaded,
    validateQuestionnaire,
    questionnaireManagementValidationErrors,
    questionnaireManagementValidationInProgress,
    getQuestionnaireDtoConvertedToOriginalDto,
    resetLocalQuestionnaire,
    handleSetLocalQuestionnaire,
    handleLocalQuestionnaireChange,
    handleExportQuestionnaire: handleExportQuestionnaireBase,
    hasQuestionnaireUnsavedChanges,
    questionnaireHasBeenChanged,
    resetQuestionnaireChangedState,
  } = useQuestionnaireController({ 
    selectedQuestionnaire: questionnaireForManagement,
    showErrorMessage,
    showMessage,
    closeMessage,
  });

  const {
    importedQuestionnaireFile,
    supportedQuestionnaireFileExtensions,
    handleCloseImportQuestionnaireManagement,
    handleImportQuestionnaireFile,
    handleImportedFileChange,
    importQuestionnaireFieldRef,
    saveImportedQuestionnaire,
    folders,
    isFoldersLoading,
  } = useImportQuestionnaireController({
    showErrorMessage,
    showInfoMessage,
    importQuestionnaire
  });

  const initialize = () => {
    getAndSetQuestionnaires().catch((err) => {
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity ?? I18n.t('Failed to get questionnaires'))
    });
  };

  React.useEffect(() => {
    initialize();
  }, []);

  const showCloseTabConfirmationMessage = () => {
    showInfoMessage(
      I18n.t('Are you sure you want to close tab without saving?'),
      {
        confirmText: I18n.t('Yes'),
        cancelText: I18n.t('No'),
        hideMessageBox: () => {
          closeMessage();
        },
        onCustomOk: () => {
          closeMessage();
          (window as any).closeFhqDesignTool();
        }
      }
    );
  };

  React.useEffect(() => {
    (window as any).onFhqDesignToolTabClose = () => {
      return new Promise<{ hasUnsavedChanges: boolean }>((resolve) => {
        const hasUnsavedChanges = hasQuestionnaireUnsavedChanges();

        if (hasUnsavedChanges) {
          showCloseTabConfirmationMessage();
        }

        resolve({
          hasUnsavedChanges,
        });
      });
    };
  }, [questionnaireForManagement, questionnaireHasBeenChanged]);

  const handleExportQuestionnaire = () => wrapCallbackWithUnsavedChangesCheck(handleExportQuestionnaireBase);

  const handleQuestionnaireDelete = async () => {
    if (!selectedQuestionnaireFromList) {
      return;
    }

    await new Promise<void>(async (resolve) => {
      showInfoMessage(
        I18n.t('Are you sure you want to delete this questionnaire?'),
        {
          confirmText: I18n.t('Yes'),
          cancelText: I18n.t('No'),
          hideMessageBox: () => {
            closeMessage();
            resolve();
          },
          onCustomOk: async () => {
            setGlobalOperationLoadingState(true);
  
            try {
              await deleteFhq4Questionnaire(selectedQuestionnaireFromList.questionnaireId);
              resetLocalQuestionnaire();
  
              try {
                await getAndSetQuestionnaires();
              } catch (err) {
                console.error(err);
                showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get questionnaires'));
              } finally {
                setSelectedQuestionnaireFromList(undefined);
                setQuestionnaireForManagement(undefined);
              }
            } catch (err) {
              console.error(err);
              showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to delete questionnaire'));
            }
  
            setGlobalOperationLoadingState(false);
            closeMessage();
            
            resolve();
          }
        }
      );
    });
  };

  const handleQuestionnaireSave = async (params?: { shouldSelectAfterCreateQuestionnaire?: boolean }) => {
    if (currentQuestionnaireIsNotCreated) {
      handleQuestionnaireCreate({
        shouldSelectAfterCreate: params?.shouldSelectAfterCreateQuestionnaire ?? true
      });
      return;
    }

    try {
      const questToUpdate = getQuestionnaireDtoConvertedToOriginalDto();

      if (!questToUpdate) {
        return;
      }
      
      setGlobalOperationLoadingState(true);

      const hasErrors = await validateQuestionnaireProcessed(questToUpdate);

      setGlobalOperationLoadingState(false);

      if (hasErrors) {
        showErrorMessage(I18n.t('questionnaireHasErrors'));

        return;
      }

      setGlobalOperationLoadingState(true);
      const updatedQuest = await saveFhq4Questionnaire(questToUpdate);
      setQuestionnaireForManagement(updatedQuest as QuestionnaireUpdateLocalDto);
      resetPagesLoadedState();
      resetQuestionnaireChangedState();
      showInfoMessage(I18n.t('Changes have been successfully applied'));

      getAndSetQuestionnaires().catch((err) => {
        console.error(err);
        showErrorMessage((err as HttpErrorDto | undefined)?.entity ?? I18n.t('Failed to get questionnaires'))
      });
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to update questionnaire'))
    } finally {
      setGlobalOperationLoadingState(false);
    }
  };

  const wrapCallbackWithUnsavedChangesCheck = (callback: () => void | Promise<void>, config?: { message?: string; shouldSelectAfterCreateQuestionnaire?: boolean }) => {
    return new Promise<void>(async (resolve) => {
      if (hasQuestionnaireUnsavedChanges()) {
        const questToUpdate = getQuestionnaireDtoConvertedToOriginalDto();

        if (!questToUpdate) {
          return;
        }

        const hasErrors = await validateQuestionnaireProcessed(questToUpdate);
        const defaultMessage = hasErrors ? I18n.t('unsavedChangesMessageWithErrors') : I18n.t('unsavedChangesMessage');

        showInfoMessage(
          config?.message || defaultMessage,
          {
            size: 'medium',
            confirmText: hasErrors ? '' : I18n.t('Save'),
            cancelText: I18n.t('Cancel'),
            additionalText: hasErrors ? I18n.t('Proceed without saving') : I18n.t("dontSave"),
            hideMessageBox: () => {
              closeMessage();
              resolve();
            },
            onAdditional: async () => {
              await callback();
              closeMessage();
              resolve();
            },
            onCustomOk: async () => {
              closeMessage();
              await handleQuestionnaireSave({
                shouldSelectAfterCreateQuestionnaire: config?.shouldSelectAfterCreateQuestionnaire
              });
              await callback();
              resolve();
            },
          }
        );
      } else {
        await callback();
        resolve();
      }
    });
  };

  const handleSelectQuestionnaireFromList = (questionnaire: QuestionnaireListCompactDto) => {
    const getQuestionnaire = async () => {
      try {
        setGlobalOperationLoadingState(true);
        setLoadingQuestionnaire(true);
        const fullInfoQuest = await getFhq4Questionnaire(questionnaire.questionnaireId);
        setQuestionnaireForManagement(fullInfoQuest as QuestionnaireUpdateLocalDto);
        handleSetLocalQuestionnaire(fullInfoQuest);
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get questionnaire'));
      } finally {
        setGlobalOperationLoadingState(false);
        setLoadingQuestionnaire(false);
      }
    };

    const doesCurrentQuestionnaireExist = !currentQuestionnaireIsNotCreated;

    wrapCallbackWithUnsavedChangesCheck(() => {
      if (currentQuestionnaireIsNotCreated) {
        removeQuestionnaireFromList(selectedQuestionnaireFromList!.questionnaireId);
      }
  
      setSelectedQuestionnaireFromList(questionnaire);
      getQuestionnaire();
    }, { shouldSelectAfterCreateQuestionnaire: doesCurrentQuestionnaireExist });
  };

  const scrollToQuestionnaire = (questionnaireId: QuestionnaireListCompactDto['questionnaireId']) => {
    const selectedQuestionnaire = questionnairesRef.current?.querySelector(`[${QUESTIONNAIRE_SELECTOR_ATTRIBUTE}="${questionnaireId}"]`);
    selectedQuestionnaire?.scrollIntoView();
  };

  const validateQuestionnaireProcessed = React.useCallback((
    questionnaire: QuestionnaireUpdateDto | QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto | QuestionnaireCreateDto
  ) => {
    return validateQuestionnaire(questionnaire, { questionnaires });
  }, [questionnaires, validateQuestionnaire]);

  const handleQuestionnaireCopy = async () => {
    try {
      const questToCopy = getQuestionnaireDtoConvertedToOriginalDto();
      
      if (questToCopy) {
        setGlobalOperationLoadingState(true);
        const hasErrors = await validateQuestionnaireProcessed(questToCopy);
        setGlobalOperationLoadingState(false);

        if (hasErrors) {
          return;
        }

        await wrapCallbackWithUnsavedChangesCheck(async () => {
          setGlobalOperationLoadingState(true);
  
          const copiedQuestionnaireId = await copyFhq4Questionnaire(questToCopy.questionnaireId);
  
          showInfoMessage(I18n.t('Questionnaire has been successfully copied'));
  
          getAndSetQuestionnaires().then((questionnaires) => {
            const createdQuestionnaire = questionnaires.find((({ questionnaireId }) => questionnaireId === copiedQuestionnaireId));
  
            if (!createdQuestionnaire) {
              return;
            }
  
            handleSelectQuestionnaireFromList(createdQuestionnaire);
            scrollToQuestionnaire(copiedQuestionnaireId);
          }).catch((err) => {
            console.error(err);
            showErrorMessage((err as HttpErrorDto | undefined)?.entity ?? I18n.t('Failed to get questionnaires'));
          })
          .finally(() => {
            setGlobalOperationLoadingState(false);
          });
        });
      }
    } catch (err) {
      setGlobalOperationLoadingState(false);
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to copy questionnaire'))
    }
  };

  const addLocalQuestionnaireToStartCreatingProcess = () => wrapCallbackWithUnsavedChangesCheck(async () =>{
    try {
      setGlobalOperationLoadingState(true);
      setLoadingQuestionnaire(true);

      const basicQuestionnaireForCreating = await getDefaultFhq4QuestionnaireForCreating();
      const localQuestionnaire = convertQuestionnaireDefaultCreateDtoToQuestionnaireCreateLocalDto(basicQuestionnaireForCreating);

      setQuestionnaireForManagement(localQuestionnaire);
      handleSetLocalQuestionnaire(localQuestionnaire);
      addQuestionnaireToList({
        questionnaireId: localQuestionnaire.questionnaireId,
        questionnaireName: localQuestionnaire.questionnaireName,
      });
      setSelectedQuestionnaireFromList(localQuestionnaire);

      const timeToWaitUntilUIIsReady = 500;
      await new Promise((r) => setTimeout(r, timeToWaitUntilUIIsReady));

      scrollToQuestionnaire(localQuestionnaire.questionnaireId);
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to create basic questionnaire'))
    } finally {
      setGlobalOperationLoadingState(false);
      setLoadingQuestionnaire(false);
    }
  });

  const handleQuestionnaireCreate = async (params: { shouldSelectAfterCreate: boolean }) => {
    try {

      const questToCreate = getQuestionnaireDtoConvertedToOriginalDto();

      if (!questToCreate) {
        return;
      }
      
      setGlobalOperationLoadingState(true);
      const hasErrors = await validateQuestionnaireProcessed(questToCreate);
      setGlobalOperationLoadingState(false);

      if (hasErrors) {
        return;
      }

      setGlobalOperationLoadingState(true);

      const createdQuestionnaire = await createFhq4Questionnaire(questToCreate as QuestionnaireCreateDto);
      setQuestionnaireForManagement(createdQuestionnaire as QuestionnaireUpdateLocalDto);

      showInfoMessage(I18n.t('Questionnaire has been successfully created'));

      getAndSetQuestionnaires()
        .then(async (questionnaires) => {
          const questionnaireAdded = questionnaires.some((({ questionnaireId }) => questionnaireId === createdQuestionnaire.questionnaireId));

          if (!questionnaireAdded) {
            return;
          }

          if (params.shouldSelectAfterCreate) {
            setQuestionnaireForManagement(createdQuestionnaire);
            handleSetLocalQuestionnaire(createdQuestionnaire);
            setSelectedQuestionnaireFromList({
              questionnaireId: createdQuestionnaire.questionnaireId,
              questionnaireName: createdQuestionnaire.questionnaireName,
            });

            const timeToWaitUntilUIIsReady = 500;
            await new Promise((r) => setTimeout(r, timeToWaitUntilUIIsReady));
  
            scrollToQuestionnaire(createdQuestionnaire.questionnaireId);
          }
        }).catch((err) => {
          console.error(err);
          showErrorMessage((err as HttpErrorDto | undefined)?.entity ?? I18n.t('Failed to get questionnaires'));
        })
        .finally(() => {
          setGlobalOperationLoadingState(false);
        });
    } catch (err) {
      setGlobalOperationLoadingState(false);
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to create questionnaire'))
    }
  };

  const getFolderFields = React.useCallback((params: GetFolderFieldsParams) => {
    return getFolderFieldsBase(params).then((fields) => {
      return fields.map((field): QuestionnaireFolderField => {
        return {
          fieldId: field.id,
          fieldName: field.name,
          fieldContext: field.context,
          fieldTypeId: field.type,
          fieldTypeName: FieldType[field.type] as keyof typeof FieldType,
          systemField: field.system,
          columnWidth: 0,
          imageURL: field.imageURL,
          options: field.options
        };
      });
    });
  }, []);

  const getFoldersForDatasheet = React.useCallback(() => {
    return getDatasheetFolders().then((x) => ([
      {
        imageURL: 'gif/dbenthi.gif',
        imageURLSelected: 'gif/dbenthi.gif',
        name: 'Datasheet Format Folders',
        id: 0,
        children: x.map((folder): QuestionnaireManagementDatasheetFolder => {
          return {
            name: folder.folderName,
            parentid: folder.parentId,
            context: folder.fieldContext,
            id: folder.folderId,
            imageURL: 'gif/folder_open.gif',
            imageURLSelected: 'gif/folder_open.gif',
          };
        })
      }
    ]));
  }, []);

  const getDatasheetFolderFormats = React.useCallback((params: GetDatasheetFolderFormats) => {
    return getDatasheetFolderFormatsBase(params).then((fields) => {
      return fields.map((x): QuestionnaireManagementDatasheetFormatListDto => {
        return {
          fieldId: x.id,
          fieldName: x.name,
          fieldContext: params.context,
          imageURL: x.imageURL,
        };
      });
    });
  }, []);

  const handleGetPage = 
    questionnaireForManagement
      ? async (pageId: QuestionnairePageUpdateDto['id']): Promise<AllQuestionnairePageGetDto> => {
          const page = await getFhq4QuestionnairePage(questionnaireForManagement.questionnaireId, pageId);

          return page;
        }
    : undefined;

  return {
    handleExportQuestionnaire,
    currentQuestionnaireIsNotCreated,
    addLocalQuestionnaireToStartCreatingProcess,
    markPageLoaded,
    handleGetPage,
    isPageLoaded,
    getDatasheetFolderFormats,
    getFoldersForDatasheet,
    getFolderFields,
    showConfirmMessage,
    validateQuestionnaireProcessed,
    questionnaireManagementValidationErrors,
    questionnaireManagementValidationInProgress,
    showErrorMessage,
    handleQuestionnaireSave,
    handleQuestionnaireCopy,
    handleLocalQuestionnaireChange,
    globalOperationIsLoading,
    loadingQuestionnaire,
    handleQuestionnaireDelete,
    closeMessage,
    messageProps,
    selectedQuestionnaireFromList,
    questionnaireForManagement,
    handleSelectQuestionnaireFromList,
    questionnaires,
    questionnairesLoading,
    questionnairesRef,
    supportedQuestionnaireFileExtensions,
    importedQuestionnaireFile,
    handleCloseImportQuestionnaireManagement,
    handleImportQuestionnaireFile,
    handleImportedFileChange,
    importQuestionnaireFieldRef,
    saveImportedQuestionnaire,
    folders,
    isFoldersLoading
  };
};